/**
 * MigrationActionDialog — SideSheet form for operator actions on a migration plan.
 *
 * Actions (ADR-102 §5):
 *   - pause    — halt promotion between canary gates
 *   - resume   — unpause a paused plan
 *   - proceed  — promote to the next canary gate (5 → 25 → 75 → 100)
 *   - abort    — rollback the migration and return tenants to the previous version
 *
 * Reason is required (ECTP Rule 7) and persisted in the audit trail.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ArrowsClockwise,
  PauseCircle,
  PlayCircle,
  XCircle,
} from '@phosphor-icons/react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '../../shared/ui/Button';
import { FieldError } from '../../shared/ui/FieldError';
import { Label } from '../../shared/ui/Label';
import { SideSheet } from '../../shared/ui/SideSheet';
import { Textarea } from '../../shared/ui/Textarea';
import { useApplyMigrationAction } from '../../features/profile-migrations/useProfileMigrations';
import {
  migrationActionFormSchema,
  type MigrationAction,
  type MigrationActionFormValues,
  type ProfileMigrationRecord,
} from '../../features/profile-migrations/types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  migration: ProfileMigrationRecord;
  action: MigrationAction;
}

const ACTION_ICON: Record<MigrationAction, JSX.Element> = {
  pause: <PauseCircle size={18} weight="regular" />,
  resume: <PlayCircle size={18} weight="regular" />,
  proceed: <ArrowsClockwise size={18} weight="regular" />,
  abort: <XCircle size={18} weight="regular" />,
};

export function MigrationActionDialog({
  open,
  onOpenChange,
  migration,
  action,
}: Props): JSX.Element {
  const { t } = useTranslation('adminMigrations');
  const applyAction = useApplyMigrationAction(migration.id);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<MigrationActionFormValues>({
    resolver: zodResolver(migrationActionFormSchema),
    defaultValues: { action, reason: '' },
  });

  // Sync the action field when the parent toggles between different operations.
  useEffect(() => {
    reset({ action, reason: '' });
  }, [action, reset, open]);

  const onSubmit = async (values: MigrationActionFormValues) => {
    await applyAction.mutateAsync(values);
    reset();
    onOpenChange(false);
  };

  const translateError = (): string | undefined => {
    const e = errors.reason;
    if (!e) return undefined;
    return t('action.reasonRequired');
  };

  const isDangerous = action === 'abort';

  return (
    <SideSheet
      open={open}
      onOpenChange={onOpenChange}
      title={t(`action.titles.${action}`, {
        subVertical: migration.subVerticalDisplayName,
        from: migration.fromVersion,
        to: migration.toVersion,
      })}
      description={t(`action.descriptions.${action}`)}
      closeLabel={t('action.cancel')}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-biz-content-lg" noValidate>
        <input type="hidden" {...register('action')} />

        {action === 'proceed' && migration.nextGatePercentage ? (
          <div className="px-biz-content-lg py-biz-content-lg rounded-biz-card bg-biz-info-subtle border border-biz-info/30 text-biz-sm text-biz-info">
            {t('action.proceedHint', {
              current: migration.canaryPercentage,
              next: migration.nextGatePercentage,
            })}
          </div>
        ) : null}

        {action === 'abort' ? (
          <div
            role="alert"
            className="px-biz-content-lg py-biz-content-lg rounded-biz-card bg-biz-danger-subtle border border-biz-danger/30 text-biz-sm text-biz-danger"
          >
            {t('action.abortWarning', {
              migrated: migration.stats.migrated,
            })}
          </div>
        ) : null}

        <div>
          <Label htmlFor="migration-action-reason">{t('action.reasonLabel')}</Label>
          <Textarea
            id="migration-action-reason"
            rows={4}
            placeholder={t('action.reasonPlaceholder')}
            invalid={Boolean(errors.reason)}
            {...register('reason')}
          />
          <FieldError id="migration-action-reason-error">{translateError()}</FieldError>
          <p className="mt-biz-content text-biz-xs text-biz-muted-foreground">
            {t('action.reasonHint')}
          </p>
        </div>

        <div className="flex items-center justify-end gap-biz-content-lg pt-biz-content-lg border-t border-biz-border">
          <Button type="button" intent="secondary" onClick={() => onOpenChange(false)}>
            {t('action.cancel')}
          </Button>
          <Button
            type="submit"
            intent={isDangerous ? 'danger' : 'primary'}
            leftIcon={ACTION_ICON[action]}
            loading={isSubmitting || applyAction.isPending}
          >
            {t(`action.submit.${action}`)}
          </Button>
        </div>
      </form>
    </SideSheet>
  );
}
